import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { FaWhatsapp } from 'react-icons/fa';
import { ChevronLeft, ChevronRight, History, ShieldCheck, Plus, FolderClosed, Edit, FileDown, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import BeautifiedErrorAlert from '../ui/BeautifiedErrorAlert';

const SalesTab = ({
    error,
    setError,
    loadBills,
    setShowCreateBill,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    dateFilter,
    setDateFilter,
    filteredBills,
    totalBills,
    currentPage,
    totalPages,
    pageSize,
    onPageChange,
    onPageSizeChange,
    handleEditBill,
    generatePDF,
    downloadPhysicalBill,
    handleDeleteBill,
    handleHoldOrder,
    handleReturnOrder,
    setShowWarrantyModal,
    handleViewPaymentHistory,
    onBack
}) => {
    const handleShare = (bill) => {
        const phone = bill.customerContactNumber ? bill.customerContactNumber.replace(/\D/g, '') : '';
        // Default to India code if missing and length is 10
        const formattedPhone = (phone.length === 10) ? `91${phone}` : phone;

        const total = bill.grandTotal || bill.totalAmount || bill.totalBillAmount || 0;
        const message = `Hello ${bill.customerName},

We hope you are doing well.

Please find attached your invoice ${bill.invoiceNumber} for an amount of ₹${total}.
Kindly review the attached PDF for complete details.

If you have any questions, feel free to contact us.

Best regards,
Jay Info Tech`;

        const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    const getBillType = (bill) => {
        const hasServices = bill.serviceBills && bill.serviceBills.length > 0;
        const items = bill.items || [];

        if (items.length > 0) {
            const hasSerialized = items.some(item =>
                (item.product?.productType === 'SERIALIZED') ||
                (item.skuProductCodes && item.skuProductCodes.length > 0)
            );

            if (hasServices) return 'Mixed (Items + Svc)';
            if (hasSerialized) return 'Serialized';
            return 'Non-Serialized';
        }

        if (hasServices) return 'Service Only';
        return 'N/A';
    };

    return (
        <div className="space-y-6">
            {error && (
                <BeautifiedErrorAlert
                    error={error}
                    onDismiss={() => { setError(null); loadBills(1, pageSize, null); }}
                />
            )}

            {/* Header & Main Actions - Ultimate Upgrade */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-2">
                <div className="flex items-center gap-5">
                    {/* Back Button */}
                    {typeof onBack === 'function' && (
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={onBack} 
                            className="h-12 w-12 rounded-2xl bg-white shadow-xl shadow-black/5 hover:bg-slate-50 active:scale-95 transition-all"
                        >
                            <ChevronLeft className="h-6 w-6 text-slate-600" />
                        </Button>
                    )}
                    <div className="space-y-1">
                        <h2 className="text-3xl font-black tracking-tight text-slate-800 dark:text-slate-100 flex items-center gap-3">
                            <History className="w-8 h-8 text-indigo-600" />
                            Bill Management
                        </h2>
                        <div className="flex items-center gap-2 px-3 py-1 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-full w-fit">
                            <ShieldCheck className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                            <p className="text-[10px] font-black uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
                                Personal Sales Repository • {totalBills} Records
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
                    <Button 
                        variant="outline" 
                        onClick={() => setShowWarrantyModal(true)} 
                        className="h-14 px-6 rounded-2xl border-slate-200 bg-white font-black text-slate-600 shadow-xl shadow-black/5 hover:bg-slate-50 active:scale-95 transition-all"
                    >
                        <ShieldCheck className="mr-3 h-5 w-5 text-indigo-500" />
                        Warranty Portal
                    </Button>
                    <Button 
                        onClick={() => setShowCreateBill(true)} 
                        className="h-14 px-8 rounded-2xl bg-black hover:bg-slate-800 text-white font-black uppercase tracking-widest shadow-2xl shadow-black/20 active:scale-95 transition-all text-xs"
                    >
                        <Plus className="mr-3 h-5 w-5" />
                        Add New Bill / Invoice
                    </Button>
                </div>
            </div>

            {/* Global Search & Filters - Ultimate Style */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-6 relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FolderClosed className="h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                    </div>
                    <Input
                        type="text"
                        placeholder="Search by client, invoice number, or email fingerprint..."
                        className="h-14 pl-12 pr-4 rounded-2xl border-slate-200 bg-white shadow-xl shadow-black/5 focus:ring-4 focus:ring-indigo-500/10 font-bold text-base transition-all"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                
                <div className="lg:col-span-3">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-14 rounded-2xl border-slate-200 bg-white shadow-xl shadow-black/5 font-bold text-slate-700 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                            <SelectValue placeholder="Lifecycle Status" />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl border-slate-100 shadow-2xl">
                            <SelectItem value="all" className="rounded-xl focus:bg-indigo-50 font-bold">Show All Stages</SelectItem>
                            <SelectItem value="pending" className="rounded-xl focus:bg-amber-50 font-bold">Awaiting Payment</SelectItem>
                            <SelectItem value="paid" className="rounded-xl focus:bg-emerald-50 font-bold">Settled / Closed</SelectItem>
                            <SelectItem value="cancelled" className="rounded-xl focus:bg-rose-50 font-bold">Voided / Cancelled</SelectItem>
                            <SelectItem value="refunded" className="rounded-xl focus:bg-sky-50 font-bold">Refund Processed</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="lg:col-span-3">
                    <Select value={dateFilter} onValueChange={setDateFilter}>
                        <SelectTrigger className="h-14 rounded-2xl border-slate-200 bg-white shadow-xl shadow-black/5 font-bold text-slate-700 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                            <SelectValue placeholder="Temporal Range" />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl border-slate-100 shadow-2xl">
                            <SelectItem value="all" className="rounded-xl focus:bg-indigo-50 font-bold">Unified History</SelectItem>
                            <SelectItem value="today" className="rounded-xl focus:bg-indigo-50 font-bold">Past 24 Hours</SelectItem>
                            <SelectItem value="yesterday" className="rounded-xl focus:bg-indigo-50 font-bold">Previous Day</SelectItem>
                            <SelectItem value="thisWeek" className="rounded-xl focus:bg-indigo-50 font-bold">Current Cycle</SelectItem>
                            <SelectItem value="thisMonth" className="rounded-xl focus:bg-indigo-50 font-bold">Monthly Summary</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>


            {/* Desktop View: Table */}
            <div className="hidden lg:block rounded-2xl border border-slate-200 bg-white dark:bg-slate-900 shadow-2xl shadow-black/5 overflow-hidden">
                <Table>
                    <TableHeader className="bg-slate-50/50 dark:bg-slate-800/50">
                        <TableRow className="hover:bg-transparent border-b-slate-200">
                            <TableHead className="w-[100px] font-black uppercase text-[10px] tracking-widest text-slate-400 py-6">Identity</TableHead>
                            <TableHead className="w-[120px] font-black uppercase text-[10px] tracking-widest text-slate-400 py-6">Invoice #</TableHead>
                            <TableHead className="font-black uppercase text-[10px] tracking-widest text-slate-400 py-6">Customer Profile</TableHead>
                            <TableHead className="font-black uppercase text-[10px] tracking-widest text-slate-400 py-6">Handler</TableHead>
                            <TableHead className="w-[110px] font-black uppercase text-[10px] tracking-widest text-slate-400 py-6">Timestamp</TableHead>
                            <TableHead className="w-[120px] font-black uppercase text-[10px] tracking-widest text-slate-400 py-6 text-center">Lifecycle</TableHead>
                            <TableHead className="w-[120px] font-black uppercase text-[10px] tracking-widest text-slate-400 py-6 text-center">Category</TableHead>
                            <TableHead className="w-[110px] font-black uppercase text-[10px] tracking-widest text-slate-400 py-6">Method</TableHead>
                            <TableHead className="w-[100px] text-right font-black uppercase text-[10px] tracking-widest text-slate-400 py-6">Valuation</TableHead>
                            <TableHead className="w-[110px] text-right font-black uppercase text-[10px] tracking-widest text-slate-400 py-6">Balance</TableHead>
                            <TableHead className="w-[180px] text-right font-black uppercase text-[10px] tracking-widest text-slate-400 py-6 pr-6">Management</TableHead>
                        </TableRow>
                    </TableHeader>

                    <TableBody>
                        {filteredBills.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} className="text-center py-16 text-muted-foreground">
                                    <div className="flex flex-col items-center gap-2">
                                        <FolderClosed className="h-10 w-10 text-slate-300" />
                                        <p>No bills found. {totalBills === 0 ? 'Create a new bill to get started.' : 'Try adjusting your filters.'}</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredBills.map(bill => {
                                if (!bill) return null;
                                const totalAmount = bill.grandTotal || bill.totalAmount || bill.totalBillAmount ||
                                    ((bill.items?.reduce((sum, item) => {
                                        return sum + (item.totalAmount || item.lineItemTotal || 0);
                                    }, 0) || 0) +
                                        (bill.serviceBills?.reduce((sum, sb) => {
                                            const cost = Number(sb.serviceCost) || 0;
                                            const tax = bill.includeGst ? (cost * (Number(sb.gstPercent) || 0) / 100) : 0;
                                            return sum + cost + tax;
                                        }, 0) || 0));

                                const formattedTotal = typeof totalAmount === 'number' ? totalAmount.toFixed(2) : totalAmount;

                                const status = (bill.paymentStatus || 'Pending').toUpperCase();
                                let statusClass = "bg-slate-100 text-slate-700 border-slate-200";
                                if (status === 'PAID') statusClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
                                else if (status === 'PENDING') statusClass = "bg-amber-50 text-amber-700 border-amber-200";
                                else if (status === 'CANCELLED') statusClass = "bg-rose-50 text-rose-700 border-rose-200";

                                return (
                                    <TableRow key={bill._id} className="group border-b-slate-100 hover:bg-indigo-50/30 dark:hover:bg-slate-900/40 transition-all duration-300">
                                        <TableCell className="font-mono text-[10px] font-black text-slate-400 group-hover:text-indigo-600 transition-colors">{bill.orderId || bill._id}</TableCell>
                                        <TableCell className="font-black text-slate-700 dark:text-slate-300 transition-colors group-hover:translate-x-1 duration-300">{bill.invoiceNumber || 'N/A'}</TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span className="font-black text-slate-900 dark:text-slate-100">{bill.customerName || 'N/A'}</span>
                                                <span className="text-[10px] font-bold text-slate-400 group-hover:text-slate-500 truncate max-w-[150px] uppercase tracking-tighter">{bill.customerEmail}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-xs font-black text-slate-500 dark:text-slate-300 uppercase tracking-widest">
                                                {bill.salesPersonName || bill.salesPersonId || 'Unknown'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-xs font-bold text-slate-500">{bill.orderDate || bill.createdAt?.split('T')[0]}</TableCell>
                                        <TableCell className="text-center">
                                            <Badge variant="outline" className={cn("font-black text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-lg border-0 shadow-sm", statusClass)}>
                                                {bill.paymentStatus || 'Pending'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {(() => {
                                                const type = getBillType(bill);
                                                let colorClass = "bg-slate-100 text-slate-700";
                                                if (type === 'Service Only') colorClass = "bg-blue-600 text-white shadow-blue-500/20";
                                                else if (type === 'Serialized') colorClass = "bg-indigo-600 text-white shadow-indigo-500/20";
                                                else if (type === 'Non-Serialized') colorClass = "bg-slate-800 text-white shadow-black/20";

                                                return <Badge variant="outline" className={cn("font-black text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-lg border-0 shadow-lg", colorClass)}>{type}</Badge>;
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span className="text-xs font-black text-slate-700 uppercase">{bill.paymentMethod || 'CASH'}</span>
                                                {bill.transactionReferenceNumber && (
                                                    <span className="text-[9px] font-bold text-slate-400 truncate max-w-[80px]" title={bill.transactionReferenceNumber}>
                                                        {bill.transactionReferenceNumber}
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>

                                        <TableCell className="text-right font-black text-slate-900 dark:text-slate-100 tabular-nums">
                                            ₹{Number(formattedTotal).toLocaleString()}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <span className={cn(
                                                "font-black tabular-nums",
                                                (Number(bill.customerPendingBill) || 0) > 0 ? "text-rose-600 drop-shadow-sm" : "text-emerald-600 opacity-60"
                                            )}>
                                                {(Number(bill.customerPendingBill) || 0) > 0 ? `₹${Number(bill.customerPendingBill).toLocaleString()}` : 'Settled'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="pr-6">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button variant="ghost" size="icon" onClick={() => handleEditBill(bill)} className="h-9 w-9 rounded-xl text-blue-600 hover:bg-blue-600 hover:text-white shadow-sm hover:shadow-blue-500/20 transition-all active:scale-90">
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => generatePDF(bill)} className="h-9 w-9 rounded-xl text-emerald-600 hover:bg-emerald-600 hover:text-white shadow-sm hover:shadow-emerald-500/20 transition-all active:scale-90">
                                                    <FileDown className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => handleViewPaymentHistory(bill.orderId || bill._id)} className="h-9 w-9 rounded-xl text-indigo-600 hover:bg-indigo-600 hover:text-white shadow-sm hover:shadow-indigo-500/20 transition-all active:scale-90">
                                                    <History className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => handleShare(bill)} className="h-9 w-9 rounded-xl text-teal-600 hover:bg-teal-600 hover:text-white shadow-sm hover:shadow-teal-500/20 transition-all active:scale-90">
                                                    <FaWhatsapp className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => handleDeleteBill(bill.orderId || bill._id || bill.id)} className="h-9 w-9 rounded-xl text-rose-600 hover:bg-rose-600 hover:text-white shadow-sm hover:shadow-rose-500/20 transition-all active:scale-90">
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>



            {/* Mobile View: Cards */}
            <div className="lg:hidden space-y-4">
                {filteredBills.length === 0 ? (
                    <div className="text-center py-12 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                        <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <FolderClosed className="h-10 w-10 text-slate-300" />
                            <p>No bills found.</p>
                        </div>
                    </div>
                ) : (
                    filteredBills.map(bill => {
                        if (!bill) return null;
                        const totalAmount = bill.grandTotal || bill.totalAmount || bill.totalBillAmount ||
                            ((bill.items?.reduce((sum, item) => {
                                return sum + (item.totalAmount || item.lineItemTotal || 0);
                            }, 0) || 0) +
                                (bill.serviceBills?.reduce((sum, sb) => {
                                    const cost = Number(sb.serviceCost) || 0;
                                    const tax = bill.includeGst ? (cost * (Number(sb.gstPercent) || 0) / 100) : 0;
                                    return sum + cost + tax;
                                }, 0) || 0));
                        const formattedTotal = typeof totalAmount === 'number' ? totalAmount.toFixed(2) : totalAmount;

                        const status = (bill.paymentStatus || 'Pending').toUpperCase();
                        let statusColor = "bg-slate-100 text-slate-700 border-slate-200";
                        if (status === 'PAID') statusColor = "bg-emerald-50 text-emerald-700 border-emerald-200";
                        else if (status === 'PENDING') statusColor = "bg-amber-50 text-amber-700 border-amber-200";
                        else if (status === 'CANCELLED') statusColor = "bg-rose-50 text-rose-700 border-rose-200";

                        return (
                            <div key={bill._id} className="group relative overflow-hidden bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-xl shadow-black/5 border border-slate-100 dark:border-slate-800 transition-all hover:-translate-y-1">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none opacity-50"></div>
                                
                                <div className="relative flex justify-between items-start mb-5">
                                    <div className="space-y-1.5">
                                        <div className="flex items-center gap-2">
                                            <span className="font-black text-[10px] text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg uppercase tracking-widest border border-indigo-100 shadow-sm">
                                                {bill.invoiceNumber || 'Manual Entry'}
                                            </span>
                                        </div>
                                        <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">{bill.customerName}</h3>
                                        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
                                            {bill.orderDate || 'No Date'}
                                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
                                            {bill.paymentMethod || 'CASH'}
                                        </div>
                                    </div>
                                    
                                    <Badge variant="outline" className={cn("font-black text-[8px] uppercase tracking-[0.2em] px-2 py-1 rounded-lg border-0 shadow-lg", statusColor)}>
                                        {bill.paymentStatus || 'Pending'}
                                    </Badge>
                                </div>

                                <div className="relative flex items-center justify-between p-4 rounded-2xl bg-slate-50/80 border border-slate-100 group-hover:bg-slate-50/40 transition-colors">
                                    <div className="space-y-0.5">
                                        <p className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-400">Total Asset Value</p>
                                        <p className="text-lg font-black text-slate-800">₹{Number(formattedTotal).toLocaleString()}</p>
                                    </div>
                                    <div className="text-right space-y-0.5">
                                        <p className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-400">Due Balance</p>
                                        <p className={cn(
                                            "text-xl font-black tabular-nums",
                                            (Number(bill.customerPendingBill) || 0) > 0 ? "text-rose-600" : "text-emerald-600"
                                        )}>
                                            ₹{Number(bill.customerPendingBill || 0).toLocaleString()}
                                        </p>
                                    </div>
                                </div>

                                <div className="relative grid grid-cols-5 gap-3 mt-5">
                                    <Button variant="outline" onClick={() => handleEditBill(bill)} className="h-12 rounded-xl border-slate-200 bg-white shadow-sm hover:bg-black hover:text-white hover:border-black active:scale-95 transition-all">
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button variant="outline" onClick={() => generatePDF(bill)} className="h-12 rounded-xl border-emerald-100 bg-emerald-50 text-emerald-600 shadow-sm hover:bg-emerald-600 hover:text-white active:scale-95 transition-all">
                                        <FileDown className="h-4 w-4" />
                                    </Button>
                                    <Button variant="outline" onClick={() => handleViewPaymentHistory(bill.orderId || bill._id)} className="h-12 rounded-xl border-indigo-100 bg-indigo-50 text-indigo-600 shadow-sm hover:bg-indigo-600 hover:text-white active:scale-95 transition-all">
                                        <History className="h-4 w-4" />
                                    </Button>
                                    <Button variant="outline" onClick={() => handleShare(bill)} className="h-12 rounded-xl border-teal-100 bg-teal-50 text-teal-600 shadow-sm hover:bg-teal-600 hover:text-white active:scale-95 transition-all">
                                        <FaWhatsapp className="h-4 w-4" />
                                    </Button>
                                    <Button variant="outline" onClick={() => handleDeleteBill(bill.orderId || bill._id || bill.id)} className="h-12 rounded-xl border-rose-100 bg-rose-50 text-rose-600 shadow-sm hover:bg-rose-600 hover:text-white active:scale-95 transition-all">
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Pagination Controls */}
            {
                totalBills > 0 && (
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-4 py-4">
                        <div className="flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto justify-between lg:justify-start">
                            <div className="text-sm text-muted-foreground whitespace-nowrap">
                                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalBills)} of {totalBills} bills
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">Rows per page:</span>
                                <Select value={pageSize.toString()} onValueChange={(value) => onPageSizeChange(Number(value))}>
                                    <SelectTrigger className="w-[80px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="5">5</SelectItem>
                                        <SelectItem value="10">10</SelectItem>
                                        <SelectItem value="20">20</SelectItem>
                                        <SelectItem value="50">50</SelectItem>
                                        <SelectItem value="100">100</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onPageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                <ChevronLeft className="h-4 w-4 mr-1" />
                                Previous
                            </Button>
                            <div className="flex items-center gap-1">
                                {totalPages <= 7 ? (
                                    // Show all pages if 7 or fewer
                                    Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <Button
                                            key={page}
                                            variant={currentPage === page ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => onPageChange(page)}
                                            className="min-w-[40px]"
                                        >
                                            {page}
                                        </Button>
                                    ))
                                ) : (
                                    // Show first, current area, and last with ellipsis
                                    (() => {
                                        const pages = [];
                                        const showPages = new Set();

                                        // Always show first page
                                        showPages.add(1);

                                        // Show pages around current page
                                        for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                                            showPages.add(i);
                                        }

                                        // Always show last page
                                        showPages.add(totalPages);

                                        // Build page array with ellipsis
                                        let lastPage = 0;
                                        Array.from(showPages).sort((a, b) => a - b).forEach(page => {
                                            if (page - lastPage > 1) {
                                                pages.push(
                                                    <span key={`ellipsis-${lastPage}`} className="px-2 text-muted-foreground">
                                                        ...
                                                    </span>
                                                );
                                            }
                                            pages.push(
                                                <Button
                                                    key={page}
                                                    variant={currentPage === page ? "default" : "outline"}
                                                    size="sm"
                                                    onClick={() => onPageChange(page)}
                                                    className="min-w-[40px]"
                                                >
                                                    {page}
                                                </Button>
                                            );
                                            lastPage = page;
                                        });

                                        return pages;
                                    })()
                                )}
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onPageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next
                                <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default React.memo(SalesTab);